import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/auth/LoginPage";
import SignupPage from "@/pages/auth/SignupPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import AcceptInvitePage from "@/pages/auth/AcceptInvitePage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import AppLayout from "@/layouts/AppLayout";
import DashboardPage from "@/pages/app/DashboardPage";
import PatientsPage from "@/pages/app/PatientsPage";
import PatientDetailPage from "@/pages/app/PatientDetailPage";
import CalendarPage from "@/pages/app/CalendarPage";
import AppointmentsPage from "@/pages/app/AppointmentsPage";
import AppointmentDetailPage from "@/pages/app/AppointmentDetailPage";
import InvoicesPage from "@/pages/app/InvoicesPage";
import InvoiceDetailPage from "@/pages/app/InvoiceDetailPage";
import FilesPage from "@/pages/app/FilesPage";
import FormsPage from "@/pages/app/FormsPage";
import ContactsPage from "@/pages/app/ContactsPage";
import PaymentsPage from "@/pages/app/PaymentsPage";
import ReportsPage from "@/pages/app/ReportsPage";
import ProductsPage from "@/pages/app/ProductsPage";
import ExpensesPage from "@/pages/app/ExpensesPage";
import SupportPage from "@/pages/app/SupportPage";
import TeamPage from "@/pages/app/TeamPage";
import SettingsPage from "@/pages/app/SettingsPage";
import MarketingPage from "@/pages/app/MarketingPage";

import AdminPage from "@/pages/AdminPage";
import BookingPage from "@/pages/public/BookingPage";
import PublicFormPage from "@/pages/public/PublicFormPage";
import UnsubscribePage from "@/pages/public/UnsubscribePage";
import PublicDocumentPage from "@/pages/public/PublicDocumentPage";
import PlatformInvitePage from "@/pages/PlatformInvitePage";
import PlatformOwnerResetPage from "@/pages/PlatformOwnerResetPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center"><div className="animate-pulse text-sm text-[#757675]">Loading...</div></div>;
  if (!user) return <Navigate to="/app/login" replace />;
  return children;
}

function AuthRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      
      {/* Public Booking Page */}
      <Route path="/book/:clinicSlug" element={<BookingPage />} />

      {/* Public patient form completion (no auth - secure token in URL) */}
      <Route path="/forms/complete/:token" element={<PublicFormPage />} />
      {/* Legacy links: /forms/{anything}?token=... */}
      <Route path="/forms/:legacyId" element={<PublicFormPage />} />

      {/* Public marketing unsubscribe (no auth - token in URL) */}
      <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />

      {/* Secure patient document view (invoice / treatment note) */}
      <Route path="/documents/:token" element={<PublicDocumentPage />} />

      {/* Platform Invite Page (public - no auth required) */}
      <Route path="/platform/invite/:token" element={<PlatformInvitePage />} />

      {/* Platform Owner Password Reset (public - one-time use) */}
      <Route path="/platform/reset-password/:token" element={<PlatformOwnerResetPage />} />

      {/* Auth */}
      <Route path="/app/login" element={<AuthRoute><LoginPage /></AuthRoute>} />
      <Route path="/app/signup" element={<AuthRoute><SignupPage /></AuthRoute>} />
      <Route path="/app/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/app/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/app/invite/:token" element={<AcceptInvitePage />} />
      <Route path="/admin" element={<AdminPage />} />

      {/* Protected app routes */}
      <Route path="/app" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="patients" element={<PatientsPage />} />
        <Route path="patients/:id" element={<PatientDetailPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="appointments" element={<AppointmentsPage />} />
        <Route path="appointments/:id" element={<AppointmentDetailPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="invoices/:id" element={<InvoiceDetailPage />} />
        <Route path="forms" element={<FormsPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="payments" element={<PaymentsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="files" element={<FilesPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="marketing" element={<MarketingPage />} />
        {/* Technical email logs live in the platform admin panel (IT Operations) only */}
        <Route path="email-activity" element={<Navigate to="/app/dashboard" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
